import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Button,
    Box,
    Grid,
    Chip,
    Divider,
    Stack,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
} from '@mui/material';
import {
    Telegram, Send, BarChart, Shield, ArrowForward, Favorite,
    AutoAwesome, Psychology, Repeat, Search, Link as LinkIcon, Speed, Stars, CheckCircle, Close as CloseIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import api from '../api/api';
import WhatshotIcon from "@mui/icons-material/Whatshot";
import CommentIcon from "@mui/icons-material/Comment";
import TagIcon from "@mui/icons-material/Tag";

interface SubscriptionSettings {
    active_account_limits: Record<string, number>;
    feature_limits: Record<string, Record<string, boolean>>;
    monthly_prices: Record<string, number>;
    discount_3_month_percent: Record<string, number>;
    discount_6_month_percent: Record<string, number>;
    discount_12_month_percent: Record<string, number>;
}

const FEATURE_TRANSLATIONS: Record<string, string> = {
    parsing: 'Парсинг: участники групп, комментаторы, сообщения и медиа из каналов в обход ограничений, аналитика',
    leadgen: 'Лидогенерация — ваши аккаунты сами предложат услуги или найдут потенциального клиента',
    warmup: 'Прогрев аккаунтов — аккаунты имитируют поведение реального человека для улучшения репутации',
    api_access: 'API доступ',
    reactors: 'Накрутка реакций с возможностью имитации живых подписчиков',
    autosubscriptions: 'Автоподписка на канал с опцией имитации реальной конверсии от рекламы',
    neurocommenting: 'Нейрокомментаторы — аккаунты комментируют сообщения в каналах, привлекая внимание к вашему продукту',
    neurocopywright: 'Нейрокопирайтинг — копируйте и агрегируйте контент даже из закрытых чатов с автоматическим рерайтом постов',
    broadcast: 'Массовые рассылки — тонко настраивайте рассылки с большим количеством поведенческих опций',
    inviter: 'Инвайтинг в чаты и каналы с выбором нескольких стратегий',
    story_mentions: 'Отметки в сторис как альтернатива инвайтингу для привлечения аудитории',
};

// Короткие названия для компактных карточек тарифов
const FEATURE_SHORT_NAMES: Record<string, string> = {
    parsing: 'Парсинг каналов, сообщений и участников групп',
    leadgen: 'Лидогенератор',
    warmup: 'Прогрев аккаунтов',
    reactors: 'Реакторы',
    autosubscriptions: 'Автоподписка на каналы',
    neurocommenting: 'Нейрокомментаторы',
    neurocopywright: 'Нейрокопирайтинг',
    broadcast: 'Массовые рассылки',
    inviter: 'Инвайтинг',
    story_mentions: 'Отметки в сториз',
};

// Для тарифов с расширенным парсингом (base и выше)
const FEATURE_SHORT_NAMES_EXTENDED: Record<string, string> = {
    ...FEATURE_SHORT_NAMES,
    parsing: 'Расширенный парсинг с аналитикой и обходом ограничений',
};

// Продающие описания для enterprise и unlimited
const PLAN_HIGHLIGHTS: Record<string, { lines: string[] }> = {
    enterprise: {
        lines: [
            'Для агентств и команд, которым нужно вести несколько проектов параллельно',
            'Больше аккаунтов в каждой задаче — выше охват за тот же промежуток времени',
            'Приоритетная поддержка',
        ],
    },
    unlimited: {
        lines: [
            'Для тех, кто работает на максимальных объёмах без компромиссов',
            'Максимальное число одновременных аккаунтов во всех задачах',
            'Приоритетная поддержка',
        ],
    },
};

// Фичи, скрытые из публичного отображения
const HIDDEN_FEATURES = new Set(['api_access']);

const PLAN_NAMES: Record<string, string> = {
    free: 'FREE',
    base: 'BASE',
    pro: 'PRO',
    enterprise: 'ENTERPRISE',
    unlimited: 'UNLIMITED',
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
    free: 'Для знакомства',
    base: 'Для старта',
    pro: 'Популярный выбор',
    enterprise: 'Максимум возможностей',
    unlimited: 'Безлимит',
};

const planOrder = ['free', 'base', 'pro', 'enterprise', 'unlimited'];

const Home: React.FC = () => {
    const [settings, setSettings] = useState<SubscriptionSettings | null>(null);
    const [plansOpen, setPlansOpen] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await api.get('/subscriptions/subscription_settings');
            setSettings(res.data);
        } catch (err) {
            console.error('Ошибка загрузки настроек:', err);
        }
    };

    return (
        <>
            {/* ===== HERO ===== */}
            <Box
                sx={{
                    bgcolor: '#1E3A8A',
                    color: 'white',
                    pt: {xs: 6, md: 10},
                    pb: {xs: 8, md: 10}, // было больше
                    minHeight: {xs: '65vh', md: '72vh'},
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start', // 🔥 вместо center
                    position: 'relative',  // ← было, но нужно убедиться
        overflow: 'hidden',    // ← добавить это
                }}
            >
                {/* Декоративные круги фона */}
                <Box sx={{
                    position: 'absolute', top: -100, right: -100,
                    width: 500, height: 500, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(96,165,250,0.15) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }} />
                <Box sx={{
                    position: 'absolute', bottom: -80, left: -80,
                    width: 400, height: 400, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(167,139,250,0.12) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }} />

                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', maxWidth: 1200, mx: 'auto' }}>
                        <Chip
                            icon={<Stars sx={{ color: '#FBBF24 !important' }} />}
                            label="Функциональный SaaS для автоматизации продвижения и роста в Telegram"
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                color: 'white',
                                mb: 4,
                                fontWeight: 600,
                                fontSize: '1rem',
                                backdropFilter: 'blur(8px)',
                            }}
                        />
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '2.6rem', md: '4.2rem' },
                                fontWeight: 900,
                                lineHeight: 1.32,
                                mb: 4,
                                background: 'linear-gradient(90deg, #60A5FA, #A78BFA)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            Typhon&nbsp;&mdash; ваше личное SMM-агентство для Telegram
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{
                                opacity: 0.92,
                                mb: 4,
                                fontWeight: 400,
                                maxWidth: 930,
                                mx: 'auto',
                                lineHeight: 1.7,
                                fontSize: { xs: '1.1rem', md: '1.35rem' },
                            }}
                        >
                            Аналитика, парсинг любых каналов и&nbsp;групп, нейрокомментаторы и&nbsp;копирайтеры, получение реакций, просмотров, рассылки, инвайтинг&nbsp;&mdash; всё в&nbsp;одном месте. Сервис продвигает и&nbsp;наполняет ваш продукт в&nbsp;Telegram за&nbsp;вас.
                        </Typography>
                           <Typography
                            variant="h5"
                            sx={{
                                opacity: 0.92,
                                mb: 4,
                                fontWeight: 400,
                                maxWidth: 930,
                                mx: 'auto',
                                lineHeight: 1.7,
                                fontSize: { xs: '1.1rem', md: '1.35rem' },
                            }}
                        >
                          Для SMM-специалистов, маркетологов, агентств, владельцев каналов, рекламных менеджеров и&nbsp;предпринимателей, и&nbsp;всех, кто хочет заработать с&nbsp;помощью услуг продвижения в&nbsp;Telegram!
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/register"
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForward />}
                            sx={{
                                bgcolor: '#60A5FA',
                                color: 'white',
                                px: 8,
                                py: 2.5,
                                fontSize: '1.25rem',
                                fontWeight: 700,
                                borderRadius: 4,
                                boxShadow: '0 8px 30px rgba(96, 165, 250, 0.45)',
                                '&:hover': {
                                    bgcolor: '#3B82F6',
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 14px 40px rgba(96, 165, 250, 0.55)',
                                },
                                transition: 'all 0.25s',
                            }}
                        >
                            Начать бесплатно прямо сейчас
                        </Button>

                        <Typography sx={{ mt: 3, opacity: 0.7, fontSize: '0.95rem' }}>
                            Без карты • 3 дня полного доступа • Отмена в один клик
                        </Typography>
                    </Box>
                </Container>
            </Box>

            <Divider />

            {/* ===== ВСЁ, ЧТО НУЖНО ДЛЯ РОСТА ===== */}
            <Box sx={{ bgcolor: '#F8FAFC', py: { xs: 8, md: 12 } }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 800, mb: 2, color: '#1E3A8A' }}>
                        Всё, что нужно для роста в Telegram
                    </Typography>
                    <Typography variant="h6" sx={{ textAlign: 'center', color: 'text.secondary', mb: 8, maxWidth: 700, mx: 'auto', fontWeight: 400 }}>
                        Комплексный набор инструментов для продвижения, аналитики и рекламы в Telegram — в одном сервисе! Привлекайте целевую аудиторию, увеличивайте охваты, масштабируйте продажи, получайте любой контент с&nbsp;помощью наших автоматизаций!
                    </Typography>

                    {(() => {
                        const tools = [
                            {
                                icon: <SmartToyIcon sx={{ fontSize: 44, color: '#F59E0B' }} />,
                                title: 'Лидогенератор',
                                desc: 'Автоматически отвечает на сообщения в чатах по ключевым словам и гео, используя ИИ-инструменты. Продвигайте свои продукты и собирайте тёплые лиды!',
                                color: '#FEF3C7',
                                accent: '#F59E0B',
                            },
                            {
                                icon: <CommentIcon sx={{ fontSize: 44, color: '#A855F7' }} />,
                                title: 'Нейрокомментаторы',
                                desc: 'Оставляют умные и естественные комментарии от лица ваших аккаунтов. периодичность, контекст, тон — всё настраивается.',
                                color: '#F5F3FF',
                                accent: '#A855F7',
                            },
                            {
                                icon: <Repeat sx={{ fontSize: 44, color: '#EC4899' }} />,
                                title: 'Нейрокопирайтер',
                                desc: 'Может собирать посты даже из закрытых каналов, переписывать под ваш стиль и автоматически публиковать в нужный канал или диалог. Контент 24/7 без вашего участия.',
                                color: '#FDF2F8',
                                accent: '#EC4899',
                            },
                            {
                                icon: <Favorite sx={{ fontSize: 44, color: '#10B981' }} />,
                                title: 'Реакторы',
                                desc: 'Ставят реакции под новыми постами нужных каналов и наращивают просмотры. Создают эффект живой аудитории — повышают охваты и доверие.',
                                color: '#ECFDF5',
                                accent: '#10B981',
                            },
                            {
                                icon: <Send sx={{ fontSize: 44, color: '#3B82F6' }} />,
                                title: 'Массовые рассылки',
                                desc: 'Настройте персонализированные сообщения в ЛС тысячам пользователей. Антибан, рандомизация, автоответчик — безопасно и эффективно.',
                                color: '#EFF6FF',
                                accent: '#3B82F6',
                            },
                            {
                                icon: <GroupAddIcon sx={{ fontSize: 44, color: '#6366F1' }} />,
                                title: 'Автоподписка на каналы',
                                desc: 'Массово подписывает ваши аккаунты на целевые каналы, демонстрируя реалистичную активность аудитории. Повысьте конверсионные показатели рекламы для ваших каналов в пару кликов!',
                                color: '#EEF2FF',
                                accent: '#6366F1',
                            },
                            {
                                icon: <Search sx={{ fontSize: 44, color: '#F59E0B' }} />,
                                title: 'Парсинг и аналитика',
                                desc: 'Собирает сообщения, участников, комментарии из любых каналов. Экспорт в Excel, аналитика по активности для каналов, поиск каналов и групп по ключевым словам',
                                color: '#FFFBEB',
                                accent: '#F59E0B',
                            },
                            {
                                icon: <WhatshotIcon sx={{ fontSize: 44, color: '#06B6D4' }} />,
                                title: 'Прогрев аккаунтов',
                                desc: 'Автоматизированный процесс прогрева аккаунтов позволяет подготовить их к работе на большую аудиторию, оркестрируя и имитируя естественное поведение живых людей',
                                color: '#ECFEFF',
                                accent: '#06B6D4',
                            },
                            {
                                icon: <PersonAddIcon sx={{ fontSize: 44, color: '#6366F1' }} />,
                                title: 'Массовый инвайтинг',
                                desc: 'Массово приглашает пользователей в ваши чаты. Поддержка нескольких стратегий, включая инвайтинг через выдачу админок!',
                                color: '#EEF2FF',
                                accent: '#6366F1',
                            },
                            {
                                icon: <TagIcon sx={{ fontSize: 44, color: '#F472B6' }} />,
                                title: 'Отметки в сториз',
                                desc: 'Продвигайте свой продукт через упоминания своей аудитории в сториз. Альтернатива инвайтам и рассылкам с возможностью создать креативную воронку продаж',
                                color: '#FDF2F8',
                                accent: '#F472B6',
                            },
                        ];

                        const remainder = tools.length % 3;
                        const mainTools = remainder === 0 ? tools : tools.slice(0, tools.length - remainder);
                        const lastTools = remainder === 0 ? [] : tools.slice(-remainder);

                        const renderCard = (tool: typeof tools[0], i: number) => (
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }} alignItems="stretch" key={i}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        borderRadius: 3,
                                        minHeight: 295,                    // ← добавь это (подбери 290–310)
                                        display: 'flex',                   // ← добавь
                                        flexDirection: 'column',
                                        border: '1px solid #E5E7EB',
                                        bgcolor: 'white',
                                        transition: 'all 0.25s',
                                        '&:hover': {
                                            transform: 'translateY(-6px)',
                                            boxShadow: `0 12px 32px ${tool.accent}22`,
                                            borderColor: tool.accent,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 68,
                                            height: 68,
                                            borderRadius: 2.5,
                                            bgcolor: tool.color,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 2.5,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {tool.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#1E3A8A', fontWeight: 700, mb: 1.5 }}>
                                        {tool.title}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.75, fontSize: '0.9rem', flexGrow: 1 }}>
                                        {tool.desc}
                                    </Typography>
                                </Paper>
                            </Grid>
                        );

                        // Для остатка 2: карточки в 1-ю и 3-ю колонки — используем пустой Grid между ними
                        const renderLastRow = () => {
                            if (lastTools.length === 0) return null;
                            if (lastTools.length === 1) {
                                return (
                                    <Grid container spacing={3} sx={{ mt: 3 }} justifyContent="center">
                                        {lastTools.map((tool, i) => renderCard(tool, mainTools.length + i))}
                                    </Grid>
                                );
                            }
                            // remainder === 2: 1-я и 3-я колонки, пустая средняя
                            return (
                                <Grid container spacing={3} sx={{ mt: 0 }}>
                                    {renderCard(lastTools[0], mainTools.length)}
                                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }} />
                                    {renderCard(lastTools[1], mainTools.length + 1)}
                                </Grid>
                            );
                        };

                        return (
                            <>
                                <Grid container spacing={3}>
                                    {mainTools.map((tool, i) => renderCard(tool, i))}
                                </Grid>
                                {renderLastRow()}
                            </>
                        );
                    })()}

                    {/* CTA под функциями */}
                    <Box sx={{ textAlign: 'center', mt: 7 }}>
                        <Button
                            component={RouterLink}
                            to="https://typhon.gitbook.io/docs"
                            variant="outlined"
                            size="large"
                            sx={{
                                px: 6,
                                py: 1.8,
                                fontWeight: 600,
                                fontSize: '1rem',
                                borderRadius: 3,
                                borderColor: '#1E40AF',
                                color: '#1E40AF',
                                '&:hover': { bgcolor: '#EFF6FF', borderColor: '#1E3A8A' },
                            }}
                        >
                            Подробнее о возможностях сервиса
                        </Button>
                    </Box>
                </Container>
            </Box>

            <Divider />

            {/* ===== КАК ЭТО РАБОТАЕТ ===== */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 800, mb: 10, color: '#1E3A8A' }}>
                    Как это работает
                </Typography>

                <Grid container spacing={4} alignItems="stretch">
                    {[
                        {
                            icon: <AccountCircleIcon sx={{ fontSize: 44, color: '#1E40AF' }} />,
                            num: '1',
                            title: 'Добавьте свои Telegram-аккаунты и прокси',
                            desc: 'В несколько кликов подключите ваши аккаунты — через tdata или официальный API Telegram.',
                        },
                        {
                            icon: <Psychology sx={{ fontSize: 44, color: '#1E40AF' }} />,
                            num: '2',
                            title: 'Настройте автоматизации',
                            desc: 'Получите нужные выгрузки с помощью парсинга, выработайте свою уникальную стратегию продвижения с помощью широкого выбора тонко настраиваемых инструментов',
                        },
                        {
                            icon: <AutoAwesome sx={{ fontSize: 44, color: '#1E40AF' }} />,
                            num: '3',
                            title: 'Запустите и наблюдайте',
                            desc: 'Typhon работает 24/7: прогревает аккаунты, собирает лиды, публикует контент, отвечает на сообщения. Вы получаете отчёты в реальном времени.',
                        },
                        {
                            icon: <BarChart sx={{ fontSize: 44, color: '#1E40AF' }} />,
                            num: '4',
                            title: 'Растите и масштабируйтесь',
                            desc: 'Увеличивайте охваты, находите целевую аудиторию или материалы для ваших каналов, подключайте новые инструменты. Ваш бизнес растет вместе с Typhon',
                        },
                    ].map((step, i) => (
                        <Grid size={{ xs: 12, md: 6, lg: 3 }} key={i}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4.5,
                                    height: '100%',
                                    textAlign: 'center',
                                    borderRadius: 3,
                                    border: '1px solid #E5E7EB',
                                    transition: 'all 0.25s',
                                    '&:hover': { transform: 'translateY(-10px)', boxShadow: 10, borderColor: '#60A5FA' },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: '50%',
                                        bgcolor: '#DBEAFE',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    {step.icon}
                                </Box>
                                <Typography variant="h2" sx={{ fontWeight: 900, color: '#1E3A8A', lineHeight: 1, mb: 2 }}>
                                    {step.num}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E3A8A', fontSize: '1rem' }}>
                                    {step.title}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '0.9rem' }}>
                                    {step.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            <Divider />

            {/* ===== ТАРИФЫ ===== */}
            <Box sx={{ bgcolor: '#F8FAFC', py: { xs: 8, md: 12 } }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 800, mb: 2, color: '#1E3A8A' }}>
                        Выберите свой тариф
                    </Typography>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 8, color: 'text.secondary', maxWidth: 700, mx: 'auto', fontWeight: 400 }}>
                        3 дня полного доступа ко всем функциям бесплатно — без карты, без обязательств
                    </Typography>

                    {/* Компактные карточки тарифов */}
                    <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                        {settings && planOrder.map((planKey) => {
                            const monthly = Number(settings.monthly_prices[planKey]) || 0;
                            const isPopular = planKey === 'pro';
                            const isFree = planKey === 'free';

                            return (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={planKey}>
                                    <Paper
                                        elevation={isPopular ? 8 : 2}
                                        sx={{
                                            p: { xs: 3, md: 3.5 },
                                            borderRadius: 4,
                                            border: isPopular ? '2px solid #60A5FA' : '1px solid #E5E7EB',
                                            position: 'relative',
                                            overflow: 'visible',
                                            transition: 'all 0.25s',
                                            '&:hover': { transform: 'translateY(-6px)', boxShadow: 10 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            minHeight: 485,
                                        }}
                                    >
                                        {isPopular && (
                                            <Chip
                                                label="ПОПУЛЯРНЫЙ"
                                                color="primary"
                                                size="small"
                                                sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', fontWeight: 700, bgcolor: '#60A5FA', zIndex: 1 }}
                                            />
                                        )}

                                        <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center', mb: 0.5 }}>
                                            {PLAN_NAMES[planKey]}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mb: 2 }}>
                                            {PLAN_DESCRIPTIONS[planKey]}
                                        </Typography>

                                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: isFree ? '#10B981' : '#1E40AF' }}>
                                                {isFree ? 'Бесплатно' : `${monthly.toLocaleString('ru-RU')} ₽`}
                                                {!isFree && <Typography component="span" variant="caption" sx={{ fontSize: '0.8rem' }}> /мес</Typography>}
                                            </Typography>
                                        </Box>

                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center', mb: 2, color: 'text.secondary' }}>
                                            {(settings.active_account_limits[planKey] ?? 0) === 999999
                                                ? '∞ аккаунтов'
                                                : `До ${settings.active_account_limits[planKey]} аккаунтов`}
                                        </Typography>

                                        {/* Кумулятивный список фич */}
                                        {(() => {
                                            const currentFeatures = settings.feature_limits[planKey] || {};
                                            const planIdx = planOrder.indexOf(planKey);
                                            const isEnterpriseOrUnlimited = planKey === 'enterprise' || planKey === 'unlimited';
                                            // base и выше используют расширенные названия
                                            const nameMap = planKey === 'free' ? FEATURE_SHORT_NAMES : FEATURE_SHORT_NAMES_EXTENDED;

                                            let inheritedFromKey: string | null = null;
                                            let newFeatureKeys: string[] = [];

                                            if (planIdx === 0) {
                                                newFeatureKeys = Object.entries(currentFeatures)
                                                    .filter(([k, v]) => v && !HIDDEN_FEATURES.has(k))
                                                    .map(([k]) => k);
                                            } else {
                                                const inheritFrom = isEnterpriseOrUnlimited ? 'pro' : planOrder[planIdx - 1];
                                                const prevFeatures = settings.feature_limits[inheritFrom] || {};
                                                // parsing считается "новым" только в base (расширенная версия), в pro и выше уже унаследован
                                                const UPGRADED_FEATURES = planKey === 'base' ? new Set(['parsing']) : new Set<string>();
                                                newFeatureKeys = Object.entries(currentFeatures)
                                                    .filter(([k, v]) => v && !HIDDEN_FEATURES.has(k) && (!prevFeatures[k] || UPGRADED_FEATURES.has(k)))
                                                    .map(([k]) => k);
                                                // Показываем "Всё из X" только если есть что наследовать помимо parsing
                                                const reallyInherited = Object.entries(prevFeatures)
                                                    .filter(([k, v]) => v && !HIDDEN_FEATURES.has(k) && !UPGRADED_FEATURES.has(k))
                                                    .length > 0;
                                                inheritedFromKey = reallyInherited ? inheritFrom : null;
                                            }

                                            const highlights = PLAN_HIGHLIGHTS[planKey];

                                            return (
                                                <Box sx={{ mb: 2, flexGrow: 1 }}>
                                                    {inheritedFromKey && (
                                                        <Box sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1, mb: 1.5,
                                                            px: 1.5, py: 0.8, borderRadius: 1.5,
                                                            bgcolor: '#EFF6FF', border: '1px solid #BFDBFE',
                                                        }}>
                                                            <CheckCircle sx={{ fontSize: 14, color: '#3B82F6' }} />
                                                            <Typography sx={{ fontSize: '0.78rem', color: '#1E40AF', fontWeight: 600 }}>
                                                                Всё из {PLAN_NAMES[inheritedFromKey]}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {newFeatureKeys.length > 0 && (
                                                        <List dense sx={{ mt: inheritedFromKey ? 0.5 : 0 }}>
                                                            {newFeatureKeys.map((key) => (
                                                                <ListItem key={key} disablePadding sx={{ py: 0.3 }}>
                                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                                        <CheckCircle fontSize="small" sx={{ color: '#10B981' }} />
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={nameMap[key] || key}
                                                                        primaryTypographyProps={{ fontSize: '0.82rem', color: 'text.secondary' }}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    )}

                                                    {/* Продающие преимущества для enterprise / unlimited */}
                                                    {highlights && (
                                                        <List dense sx={{ mt: newFeatureKeys.length > 0 ? 0.5 : 0 }}>
                                                            {highlights.lines.map((line, i) => (
                                                                <ListItem key={i} disablePadding sx={{ py: 0.3 }}>
                                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                                        <CheckCircle fontSize="small" sx={{ color: '#10B981' }} />
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={line}
                                                                        primaryTypographyProps={{ fontSize: '0.82rem', color: 'text.secondary' }}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    )}
                                                </Box>
                                            );
                                        })()}

                                        <Button
                                            component={RouterLink}
                                            to="/register"
                                            variant={isPopular ? 'contained' : 'outlined'}
                                            fullWidth
                                            sx={{
                                                mt: 'auto',
                                                py: 1.2,
                                                fontWeight: 700,
                                                borderRadius: 2.5,
                                                bgcolor: isPopular ? '#60A5FA' : 'transparent',
                                                borderColor: '#60A5FA',
                                                color: isPopular ? 'white' : '#1E40AF',
                                                '&:hover': { bgcolor: isPopular ? '#3B82F6' : '#EFF6FF' },
                                            }}
                                        >
                                            {isFree ? 'Попробовать' : 'Выбрать'}
                                        </Button>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {/* Кнопка подробнее о тарифах */}
                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => setPlansOpen(true)}
                            sx={{
                                px: 6,
                                py: 1.8,
                                fontWeight: 600,
                                fontSize: '1rem',
                                borderRadius: 3,
                                borderColor: '#1E40AF',
                                color: '#1E40AF',
                                '&:hover': { bgcolor: '#EFF6FF', borderColor: '#1E3A8A' },
                            }}
                        >
                            Подробнее о тарифах и скидках
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* ===== МОДАЛКА ТАРИФОВ ===== */}
            <Dialog
                open={plansOpen}
                onClose={() => setPlansOpen(false)}
                maxWidth="xl"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, maxHeight: '92vh', overflow: 'visible' } }}
            >
                <DialogTitle sx={{ borderBottom: '1px solid #E5E7EB', pb: 3, pr: 7, position: 'relative',  zIndex: 20   // ← добавлено
       }}>
                    <Typography variant="h4" fontWeight={800} textAlign="center" color="#1E3A8A">
                        Тарифы и цены
                    </Typography>
                    <Typography variant="body1" textAlign="center" color="text.secondary" mt={0.5}>
                        Скидки до 20% при оплате вперёд
                    </Typography>
                    <IconButton onClick={() => setPlansOpen(false)} sx={{ position: 'absolute', right: 16, top: 16 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ py: 6 }}>
                    <Grid container spacing={3} sx={{ mt: 3 }}>
                        {settings && planOrder.map((planKey) => {
                            const monthly = Number(settings.monthly_prices[planKey]) || 0;
                            const d3 = Number(settings.discount_3_month_percent[planKey]) || 0;
                            const d6 = Number(settings.discount_6_month_percent[planKey]) || 0;
                            const d12 = Number(settings.discount_12_month_percent[planKey]) || 0;
                            const accountLimit = settings.active_account_limits[planKey];
                            const features = settings.feature_limits[planKey] || {};
                            const isPopular = planKey === 'pro';
                            const isFree = planKey === 'free';

                            const discountPercent3 = monthly > 0 ? Math.round((1 - d3 / (monthly * 3)) * 100) : 0;
                            const discountPercent6 = monthly > 0 ? Math.round((1 - d6 / (monthly * 6)) * 100) : 0;
                            const discountPercent12 = monthly > 0 ? Math.round((1 - d12 / (monthly * 12)) * 100) : 0;

                            return (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={planKey}>
                                    <Paper
                                        elevation={isPopular ? 10 : 2}
                                        sx={{
                                            p: 3.5,
                                            pt: 7,
                                            borderRadius: 3,
                                            border: isPopular ? '2px solid #60A5FA' : '1px solid #E5E7EB',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            position: 'relative',
                                            overflow: 'visible',           // ← добавлено

                                        }}
                                    >
                                        {isPopular && (
                                            <Chip label="ПОПУЛЯРНЫЙ" color="primary" size="small"
                                                sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 700, zIndex: 10 }} />
                                        )}

                                        <Typography variant="h5" fontWeight={800} textAlign="center" mb={0.5}>
                                            {PLAN_NAMES[planKey]}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', mb: 2 }}>
                                            {PLAN_DESCRIPTIONS[planKey]}
                                        </Typography>

                                        {/* Цена */}
                                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                                            <Typography variant="h4" fontWeight={900} color={isFree ? '#10B981' : '#1E40AF'}>
                                                {isFree ? 'Бесплатно' : `${monthly.toLocaleString('ru-RU')} ₽`}
                                                {!isFree && <Typography component="span" variant="caption" sx={{ fontSize: '0.75rem' }}>/мес</Typography>}
                                            </Typography>
                                        </Box>

                                        {/* Скидки */}
                                        {!isFree && monthly > 0 && (
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                                                <Typography variant="caption" fontWeight={700} color="#10B981" display="block" mb={0.5}>
                                                    Скидки при оплате вперёд:
                                                </Typography>
                                                <Stack spacing={0.5}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                                                        <Typography variant="body2" color="text.secondary">3 месяца</Typography>
                                                        <Typography variant="body2" fontWeight={700}>{d3.toLocaleString('ru-RU')} ₽ <Chip label={`−${discountPercent3}%`} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: '#DCFCE7', color: '#16A34A' }} /></Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                                                        <Typography variant="body2" color="text.secondary">6 месяцев</Typography>
                                                        <Typography variant="body2" fontWeight={700}>{d6.toLocaleString('ru-RU')} ₽ <Chip label={`−${discountPercent6}%`} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: '#DCFCE7', color: '#16A34A' }} /></Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                                                        <Typography variant="body2" color="text.secondary">12 месяцев</Typography>
                                                        <Typography variant="body2" fontWeight={700}>{d12.toLocaleString('ru-RU')} ₽ <Chip label={`−${discountPercent12}%`} size="small" sx={{ height: 18, fontSize: '0.7rem', bgcolor: '#DCFCE7', color: '#16A34A' }} /></Typography>
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        )}

                                        {/* Аккаунты */}
                                        <Typography variant="subtitle2" fontWeight={700} textAlign="center" mb={1.5} color="text.primary">
                                            {accountLimit === 999999 ? '∞ аккаунтов' : `До ${accountLimit} аккаунтов`}
                                        </Typography>

                                        {/* Полный список фич */}
                                        <List dense sx={{ flexGrow: 1 }}>
                                            {Object.entries(features).filter(([key]) => !HIDDEN_FEATURES.has(key)).map(([key, enabled]) => (
                                                <ListItem key={key} disablePadding sx={{ py: 0.4 }}>
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        {enabled
                                                            ? <CheckCircle fontSize="small" sx={{ color: '#10B981' }} />
                                                            : <CloseIcon fontSize="small" sx={{ color: '#EF4444', opacity: 0.5 }} />
                                                        }
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={FEATURE_TRANSLATIONS[key] || key}
                                                        primaryTypographyProps={{
                                                            fontSize: '0.82rem',
                                                            color: enabled ? 'text.primary' : 'text.disabled',
                                                            lineHeight: 1.4,
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>

                                        <Button
                                            component={RouterLink}
                                            to="/register"
                                            variant={isPopular ? 'contained' : 'outlined'}
                                            fullWidth
                                            onClick={() => setPlansOpen(false)}
                                            sx={{
                                                mt: 2,
                                                py: 1.2,
                                                fontWeight: 700,
                                                borderRadius: 2.5,
                                                bgcolor: isPopular ? '#60A5FA' : 'transparent',
                                                borderColor: '#60A5FA',
                                                color: isPopular ? 'white' : '#1E40AF',
                                                '&:hover': { bgcolor: isPopular ? '#3B82F6' : '#EFF6FF' },
                                            }}
                                        >
                                            {isFree ? 'Начать бесплатно' : 'Попробовать 3 дня бесплатно'}
                                        </Button>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{
                    p: 3,
                    borderTop: '1px solid #E5E7EB',
                    justifyContent: 'center',
                    position: 'sticky',     // ← важно
                    bottom: 0,
                    bgcolor: 'white',
                    zIndex: 10
                }}>
                    <Button onClick={() => setPlansOpen(false)} size="large" variant="outlined" sx={{ borderRadius: 3, px: 6 }}>
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            <Divider />

            {/* ===== ПОЧЕМУ ВЫБИРАЮТ ===== */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <Typography
                    variant="h3"
                    sx={{
                        textAlign: 'center',
                        fontWeight: 800,
                        mb: 8,
                        color: '#1E3A8A',
                        fontSize: { xs: '1.9rem', sm: '2.4rem', md: '2.8rem' },
                    }}
                >
                    Почему стоит выбрать наш сервис?
                </Typography>

                <Grid container spacing={3}>
                    {[
                        {
                            icon: <Speed sx={{ fontSize: 44, color: '#10B981' }} />,
                            title: 'Экономия времени',
                            desc: 'То, что делали вручную 20 часов — теперь делает Typhon за 20 минут.',
                        },
                        {
                            icon: <Shield sx={{ fontSize: 44, color: '#10B981' }} />,
                            title: 'Безопасность',
                            desc: 'Умные задержки, прокси, ротация аккаунтов — ваши профили под защитой.',
                        },
                        {
                            icon: <BarChart sx={{ fontSize: 44, color: '#10B981' }} />,
                            title: 'Непрерывная работа',
                            desc: 'Для работы с Typhon вам даже не нужен под рукой компьютер — настраивайте и мониторьте работу ваших аккаунтов в браузере!',
                        },
                        {
                            icon: <AutoAwesome sx={{ fontSize: 44, color: '#10B981' }} />,
                            title: 'Удобство и контроль',
                            desc: 'Тонкие настройки автоматизаций и управление аккаунтами позволят выработать комплексную стратегию продвижения в Telegram.',
                        },
                    ].map((item, i) => (
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={i}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    height: '100%',
                                    border: '1px solid #E5E7EB',
                                    borderRadius: 3,
                                    transition: 'all 0.25s',
                                    '&:hover': { transform: 'translateY(-8px)', boxShadow: 8, borderColor: '#10B981' },
                                }}
                            >
                                <Box sx={{
                                    width: 68,
                                    height: 68,
                                    borderRadius: '50%',
                                    bgcolor: '#ECFDF5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2.5,
                                }}>
                                    {item.icon}
                                </Box>
                                <Typography variant="h6" sx={{ color: '#1E40AF', fontWeight: 700, mb: 1.5 }}>
                                    {item.title}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '0.9rem' }}>
                                    {item.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ===== КАК НАЧАТЬ ===== */}
            <Box sx={{ bgcolor: '#1E293B', color: 'white', py: { xs: 8, md: 12 } }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        sx={{ textAlign: 'center', fontWeight: 800, mb: 2, fontSize: { xs: '1.9rem', md: '2.6rem' } }}
                    >
                        Как начать
                    </Typography>
                    <Typography
                        sx={{ textAlign: 'center', opacity: 0.65, mb: 8, fontSize: '1.05rem', maxWidth: 600, mx: 'auto' }}
                    >
                        Три шага до первой автоматизации — займёт меньше пяти минут
                    </Typography>

                    <Grid container spacing={3} sx={{ mb: 8 }}>
                        {[
                            {
                                num: '01',
                                title: 'Зарегистрируйтесь',
                                desc: 'Создайте аккаунт на сайте — без карты и без обязательств. Вход за несколько секунд.',
                                kind: 'link' as const,
                                to: '/register',
                                label: 'Зарегистрироваться',
                                variant: 'contained' as const,
                            },
                            {
                                num: '02',
                                title: 'Авторизуйтесь в боте',
                                desc: 'Зайдите в наш Telegram-бот @tg_typhon_bot и авторизуйтесь — получите бесплатный триал на 3 дня с полным доступом ко всем функциям.',
                                kind: 'href' as const,
                                href: 'https://t.me/tg_typhon_bot',
                                label: '@tg_typhon_bot',
                                variant: 'outlined' as const,
                            },
                            {
                                num: '03',
                                title: 'Испытайте все возможности',
                                desc: 'Добавляйте аккаунты, настраивайте автоматизации и запускайте инструменты. Пользуйтесь подробными инструкциями из нашей базы знаний!',
                                kind: 'link' as const,
                                to: 'https://typhon.gitbook.io/docs/',
                                label: 'База знаний',
                                variant: 'outlined' as const,
                            },
                        ].map((step, i) => (
                            <Grid size={{ xs: 12, md: 4 }} key={i}>
                                <Box
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        borderRadius: 3,
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        bgcolor: 'rgba(255,255,255,0.04)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.07)',
                                            borderColor: '#60A5FA',
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '2.8rem',
                                            fontWeight: 900,
                                            color: '#60A5FA',
                                            opacity: 0.35,
                                            lineHeight: 1,
                                            mb: 2,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {step.num}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                                        {step.title}
                                    </Typography>
                                    <Typography sx={{ opacity: 0.65, lineHeight: 1.8, fontSize: '0.9rem', flexGrow: 1, mb: 3 }}>
                                        {step.desc}
                                    </Typography>
                                    {step.kind === 'href' ? (
                                        <Button
                                            component="a"
                                            href={step.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant={step.variant}
                                            startIcon={<Telegram />}
                                            sx={{
                                                alignSelf: 'flex-start',
                                                borderColor: 'rgba(255,255,255,0.3)',
                                                color: 'white',
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                '&:hover': { borderColor: '#60A5FA', bgcolor: 'rgba(96,165,250,0.1)' },
                                            }}
                                        >
                                            {step.label}
                                        </Button>
                                    ) : (
                                        <Button
                                            component={RouterLink}
                                            to={step.to}
                                            variant={step.variant}
                                            sx={step.variant === 'contained' ? {
                                                alignSelf: 'flex-start',
                                                bgcolor: '#60A5FA',
                                                color: 'white',
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                '&:hover': { bgcolor: '#3B82F6' },
                                            } : {
                                                alignSelf: 'flex-start',
                                                borderColor: 'rgba(255,255,255,0.3)',
                                                color: 'white',
                                                borderRadius: 2,
                                                fontWeight: 600,
                                                '&:hover': { borderColor: '#60A5FA', bgcolor: 'rgba(96,165,250,0.1)' },
                                            }}
                                        >
                                            {step.label}
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Нижняя строка — TG канал */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 2,
                            pt: 6,
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                        }}
                    >
                        <Typography sx={{ opacity: 0.6, fontSize: '0.95rem' }}>
                            Следите за обновлениями и новыми функциями:
                        </Typography>
                        <Button
                            href="https://t.me/tg_typhon"
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<Telegram />}
                            sx={{
                                color: '#60A5FA',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                textDecoration: 'none',
                                px: 2,
                                py: 0.8,
                                borderRadius: 2,
                                '&:hover': { bgcolor: 'rgba(96,165,250,0.1)' },
                            }}
                        >
                            Подписаться на Telegram-канал
                        </Button>
                    </Box>
                </Container>
            </Box>
        </>
    );
};

export default Home;